import { useFieldArray, useFormContext } from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import InputFormField from "components/input-form-field";
import PhoneMaskedInput from "components/input-form-field/phone-masked-input.component";
import SelectFormField from "components/select-form-field";

import { ContactFormValues, EMPTY_PHONE_ROW } from "./contact-form.validator";
import { CONTACT_KINDS, CONTACT_KIND_LABELS } from "./contact-kind.util";

const KIND_OPTIONS = CONTACT_KINDS.map((kind) => ({
  value: kind,
  label: CONTACT_KIND_LABELS[kind]
}));

type ContactFieldsProps = {
  // "attach" is SaveContactDialog's subset: the call's number is the
  // contact's number, so no phone rows and no email.
  variant?: "full" | "attach";
  autoFocusName?: boolean;
};

// The one contact form, shared by the Rolodex create/edit dialog and the
// unknown-caller "Save to Rolodex" dialog. Must sit inside a FormProvider
// over ContactFormValues validated by `contactValidator`. The full form
// runs in two columns from the `sm` breakpoint — who they are on the left,
// their numbers on the right — so a 720 px dialog is not one long scroll.
export default function ContactFields({
  variant = "full",
  autoFocusName = false
}: ContactFieldsProps) {
  const {
    control,
    formState: { errors }
  } = useFormContext<ContactFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "phone_numbers"
  });
  const isFull = variant === "full";
  const listError =
    errors.phone_numbers?.root?.message ??
    (errors.phone_numbers as { message?: string } | undefined)?.message;

  const nameField = (
    <InputFormField
      name="name"
      label="Name"
      control={control}
      required
      autoFocus={autoFocusName}
      inputProps={{ maxLength: 255, "data-testid": "contact-field-name" }}
    />
  );
  const organizationField = (
    <InputFormField
      name="organization"
      label="Organization"
      control={control}
      inputProps={{
        maxLength: 255,
        "data-testid": "contact-field-organization"
      }}
    />
  );
  const notesField = (
    <InputFormField
      name="notes"
      label="Notes"
      control={control}
      multiline
      minRows={2}
      inputProps={{ "data-testid": "contact-field-notes" }}
    />
  );

  if (!isFull) {
    return (
      <Stack spacing={2}>
        {nameField}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {organizationField}
          <SelectFormField
            name="kind"
            label="Kind"
            control={control}
            options={KIND_OPTIONS}
            disableClear
            sx={{ minWidth: 200 }}
          />
        </Stack>
        {notesField}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
        <Stack spacing={2} sx={{ flex: 4, minWidth: 0 }}>
          {nameField}
          {organizationField}
          <SelectFormField
            name="kind"
            label="Kind"
            control={control}
            options={KIND_OPTIONS}
            disableClear
          />
          <InputFormField
            name="email"
            label="Email"
            type="email"
            control={control}
            inputProps={{
              maxLength: 254,
              "data-testid": "contact-field-email"
            }}
          />
        </Stack>
        <Stack
          spacing={1}
          sx={{ flex: 5, minWidth: 0 }}
          data-testid="contact-phone-rows"
        >
          <InputLabel shrink component="span" required sx={{ mb: 0 }}>
            Phone numbers
          </InputLabel>
          {fields.map((field, index) => (
            <Stack
              key={field.id}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              data-testid={`contact-phone-row-${index}`}
            >
              <Box sx={{ flex: 3, minWidth: 0 }}>
                <InputFormField
                  name={`phone_numbers.${index}.number`}
                  label="Number"
                  placeholder="(555) 123-4567"
                  control={control}
                  required
                  inputComponent={PhoneMaskedInput}
                  inputProps={{
                    "data-testid": `contact-phone-number-${index}`,
                    inputMode: "tel"
                  }}
                />
              </Box>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <InputFormField
                  name={`phone_numbers.${index}.label`}
                  label="Label"
                  placeholder="Main, Claims, Cell…"
                  control={control}
                  inputProps={{
                    maxLength: 50,
                    "data-testid": `contact-phone-label-${index}`
                  }}
                />
              </Box>
              <Tooltip
                title={
                  fields.length === 1
                    ? "A contact needs at least one number"
                    : `Remove number ${index + 1}`
                }
              >
                <span style={{ alignSelf: "flex-end" }}>
                  <IconButton
                    aria-label={`Remove number ${index + 1}`}
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                    data-testid={`contact-phone-remove-${index}`}
                    sx={{ mb: 0.5 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
          {listError && (
            <Typography
              variant="caption"
              color="error"
              data-testid="contact-phone-rows-error"
            >
              {listError}
            </Typography>
          )}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => append({ ...EMPTY_PHONE_ROW })}
            sx={{ alignSelf: "flex-start" }}
            data-testid="contact-phone-add"
          >
            Add number
          </Button>
        </Stack>
      </Stack>
      {notesField}
    </Stack>
  );
}
